import { FormHelperText } from '@mui/material';
import { useEditor } from '@tiptap/react';
import clsx from 'clsx';
import React from 'react';

import { getMessageEditorExtensions } from './messageEditorExtensions';
import { RichTextInput } from './RichTextInput';

const TOOLBAR_ACTIONS = [
  'bold',
  'italic',
  'underline',
  'strike',
  'bulletList',
  'orderedList',
  'link',
];

interface MessageBodyEditorProps {
  name?: string;
  value: string;
  onChange: (markdown: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
}

export const MessageBodyEditor: React.FC<MessageBodyEditorProps> = ({
  name,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  placeholder,
}) => {
  const tiptapEditor = useEditor({
    extensions: getMessageEditorExtensions({ placeholder }),
    editorProps: {
      attributes: {
        class: 'flex flex-col gap-1 min-h-[64px] relative',
      },
    },
    content: value,
    onUpdate: ({ editor }) => {
      // Trim trailing newlines added by tiptap-markdown to avoid
      // false dirty state when content hasn't actually changed.
      const markdown = editor.storage.markdown
        .getMarkdown()
        .replace(/\n+$/, '');
      onChange(markdown);
    },
    onBlur: () => {
      if (name) {
        // Construct a synthetic event with the field name so Formik
        // can track touched state for this field.
        onBlur?.({
          target: { name },
        } as unknown as React.FocusEvent<HTMLElement>);
      } else {
        onBlur?.({} as React.FocusEvent<HTMLElement>);
      }
    },
  });

  // Sync external value changes (e.g. form reset) into the editor
  React.useEffect(() => {
    if (!tiptapEditor) return;
    // Trim trailing newlines to match the format stored by onChange
    const currentMarkdown = tiptapEditor.storage.markdown
      .getMarkdown()
      .replace(/\n+$/, '');
    if (value !== currentMarkdown) {
      tiptapEditor.commands.setContent(value);
    }
  }, [tiptapEditor, value]);

  if (!tiptapEditor) return null;

  return (
    <div>
      <div
        className={clsx(
          'relative box-border border border-solid rounded [&>div>div]:z-modal!',
          {
            'border-black-heading': tiptapEditor.isFocused,
            'border-non-hover-border': !tiptapEditor.isFocused,
            'border-red-500': error && !tiptapEditor.isFocused,
          },
        )}
      >
        <RichTextInput
          editor={tiptapEditor}
          showHighlightToolbar={false}
          supportActions={{
            toolbar: TOOLBAR_ACTIONS,
          }}
          classes={{
            container: 'px-3.5 py-3 max-h-64 overflow-auto',
            toolbar: 'rounded-t',
          }}
        />
      </div>
      {helperText && <FormHelperText error>{helperText}</FormHelperText>}
    </div>
  );
};
